import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { LevelBadge } from "@/components/LevelBadge";
import { EmptyState } from "@/components/common";
import { formatDate } from "@/lib/format";
import { FlaskConical, ShieldCheck, Power, RotateCcw, ListMusic, AlertTriangle } from "lucide-react";

const ACT = { taught: "bg-[#C85A32] text-white", reviewed: "bg-[#FEF3C7] text-[#92600A]", danced: "bg-secondary text-[#5C4A42]" };

function Timeline({ data, side }) {
  if (!data?.length) return <EmptyState title="No history" subtitle={side === "old" ? "No legacy history." : "No unified sessions."} />;
  return (
    <div className="space-y-3">
      {data.map((g) => (
        <div key={g.date} className="rounded-2xl border border-border bg-card p-3" data-testid={`pilot-${side}-date-${g.date}`}>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-bold text-[#B04B26]">{formatDate(g.date, { weekday: "short", year: true })}</p>
            {g.has_playlist && <span className="inline-flex items-center gap-1 text-xs text-[#6E5B51]"><ListMusic size={12} /> Playlist</span>}
          </div>
          <ul className="space-y-1.5">
            {g.dances.map((x) => (
              <li key={x.dance.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-secondary/40 px-2.5 py-1.5 text-sm">
                <span className="flex-1 font-semibold text-[#2D1E18]">{x.dance.name}</span>
                {(x.activities || []).map((a) => <span key={a} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ACT[a] || "bg-secondary"}`}>{a}</span>)}
                {x.tag && <span className="rounded-full bg-[#2D1E18] px-2 py-0.5 text-[10px] font-bold uppercase text-white">{x.tag}</span>}
                <LevelBadge level={x.dance.level} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function PilotPanel() {
  const qc = useQueryClient();
  const { data: classes } = useQuery({ queryKey: ["classes-all"], queryFn: async () => (await api.get("/classes")).data });
  const { data: status } = useQuery({ queryKey: ["pilot-status"], queryFn: async () => (await api.get("/admin/us/pilot")).data });
  const pilotId = status?.pilot_class_id || "";
  const { data: cmp } = useQuery({ queryKey: ["pilot-compare", pilotId], enabled: !!pilotId, queryFn: async () => (await api.get(`/admin/us/pilot/compare?class_id=${pilotId}`)).data });

  const set = useMutation({
    mutationFn: async (body) => (await api.put("/admin/us/pilot", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pilot-status"] }),
  });
  const clear = useMutation({
    mutationFn: async () => (await api.post("/admin/us/pilot/clear")).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pilot-status"] }),
  });

  const setPilot = (cid) => set.mutate({ pilot_class_id: cid, members_enabled: false, admin_preview_enabled: true });
  const toggleMembers = () => {
    const next = !status?.members_enabled;
    if (next && !window.confirm("STAGE 2: enable the unified view for ORDINARY MEMBERS of this one class? Click OK only when you've approved going beyond admin-only testing.")) return;
    set.mutate({ pilot_class_id: pilotId, members_enabled: next, admin_preview_enabled: true });
  };

  return (
    <div className="mb-8 rounded-2xl border-2 border-[#8C5BA6]/40 bg-[#FBF7FD] p-4" data-testid="pilot-panel">
      <div className="mb-3 flex items-center gap-2">
        <FlaskConical size={18} className="text-[#7A3E97]" />
        <h2 className="font-display text-lg font-extrabold text-[#2D1E18]">Single-Class Pilot (admin-only testing)</h2>
      </div>
      <p className="mb-3 flex items-start gap-2 rounded-xl bg-[#F3E8FA] px-3 py-2 text-xs text-[#6B3E86]">
        <ShieldCheck size={16} className="mt-0.5 shrink-0" />
        <span>Unified reads = <code className="font-mono">bf_sessions</code> (historical) merged with <code className="font-mono">us_sessions</code> (builder), computed at read-time. Nothing promoted. Stage 1 affects admins only; ordinary members stay on legacy until you flip Members ON. Global flags stay off.</span>
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase text-[#6E5B51]">Pilot class</label>
          <select className="min-h-11 rounded-xl border border-border bg-card px-3 py-2 text-sm" value={pilotId}
            onChange={(e) => (e.target.value ? setPilot(e.target.value) : clear.mutate())} data-testid="pilot-class-select">
            <option value="">— none (legacy for all) —</option>
            {(classes || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {pilotId && (
          <>
            <button onClick={toggleMembers} data-testid="pilot-members-toggle"
              className={`inline-flex min-h-11 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold ${status?.members_enabled ? "bg-emerald-600 text-white" : "border border-border bg-card text-[#2D1E18]"}`}>
              <Power size={15} /> Members: {status?.members_enabled ? "ON (stage 2)" : "OFF (admin-only)"}
            </button>
            <button onClick={() => { if (window.confirm("Rollback the pilot? Everyone returns to legacy immediately.")) clear.mutate(); }}
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-bold text-[#2D1E18]" data-testid="pilot-clear-btn">
              <RotateCcw size={15} /> Rollback
            </button>
          </>
        )}
      </div>

      {pilotId && (
        <p className="mt-3 text-xs text-[#6E5B51]" data-testid="pilot-status-line">
          Active pilot: <strong>{status?.pilot_class_name}</strong> · sandbox {status?.sandbox_sessions} · builder {status?.builder_sessions} ·
          {status?.members_enabled ? <span className="font-bold text-emerald-700"> members see unified</span> : <span> admins only</span>}
        </p>
      )}

      {status?.members_enabled && (
        <p className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
          <AlertTriangle size={14} /> Stage 2 is live — ordinary members of this class now see the unified view.
        </p>
      )}

      {pilotId && cmp && (
        <div className="mt-5 space-y-6">
          <div>
            <h3 className="mb-2 font-display font-extrabold text-[#2D1E18]">Dashboard "New Dances" — taught only</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-3" data-testid="pilot-nd-old">
                <p className="mb-2 text-xs font-bold uppercase text-[#6E5B51]">Legacy (any posted)</p>
                <ul className="space-y-1 text-sm">{(cmp.new_dances.old || []).map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name}</li>)}</ul>
              </div>
              <div className="rounded-2xl border border-[#7A3E97] bg-card p-3" data-testid="pilot-nd-new">
                <p className="mb-2 text-xs font-bold uppercase text-[#7A3E97]">Unified (taught-in-completed only)</p>
                <ul className="space-y-1 text-sm">{(cmp.new_dances.new || []).length ? cmp.new_dances.new.map((d) => <li key={d.id} className="font-semibold text-[#2D1E18]">{d.name}</li>) : <li className="text-[#6E5B51]">None</li>}</ul>
              </div>
            </div>
          </div>
          <div>
            <h3 className="mb-2 font-display font-extrabold text-[#2D1E18]">Class history</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div data-testid="pilot-hist-old"><p className="mb-2 text-xs font-bold uppercase text-[#6E5B51]">Legacy</p><Timeline data={cmp.history.old} side="old" /></div>
              <div data-testid="pilot-hist-new"><p className="mb-2 text-xs font-bold uppercase text-[#7A3E97]">Unified (pilot)</p><Timeline data={cmp.history.new} side="new" /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
